import {
  ASSET_OWNER_PAYOUT_TRANSFER_DAYS,
  ASSET_OWNER_US_ACCOUNT_TYPES,
  assetOwnerPayoutTransferDayOptionKey,
  digitsOnly,
  extractAssetOwnerPayoutFromPayload,
  type AssetOwnerPayoutTransferDay,
  type AssetOwnerUsAccountType,
} from '../utils/assetOwnerPayoutBank';
import { useI18n } from '../i18n';
import { formInputClassName, formInputDir } from './formInputDirection';
import directionStyles from './formInputDirection.module.css';
import styles from './AssetOwnerBankDetailsSection.module.css';

export type AssetOwnerBankDetailsSectionProps = {
  payload: Record<string, unknown>;
  disabled?: boolean;
  /** When true, redacted account numbers stay read-only so partial payment edits stay safe. */
  lockMaskedAccountNumber?: boolean;
  onChange: (patch: Record<string, unknown>) => void;
};

export function AssetOwnerBankDetailsSection({
  payload,
  disabled = false,
  lockMaskedAccountNumber = false,
  onChange,
}: AssetOwnerBankDetailsSectionProps) {
  const { t, language } = useI18n();
  const fields = extractAssetOwnerPayoutFromPayload(payload);
  const isIsrael = fields.payout_country === 'IL';
  const accountNumberLocked =
    lockMaskedAccountNumber && fields.account_number.includes('*');

  const patchField = (key: string, value: string | number) => {
    onChange({ [key]: value });
  };

  return (
    <section
      className={styles.section}
      aria-labelledby="asset-owner-bank-title"
      dir={language === 'he' ? 'rtl' : 'ltr'}
    >
      <h3 id="asset-owner-bank-title" className={styles.title}>
        {t('register.assetOwnerPayout.title')}
      </h3>

      <div className={styles.grid}>
        <div className={styles.field}>
          <label className={styles.label} htmlFor="asset-owner-account-holder">
            {t('register.assetOwnerPayout.accountHolderName')}
            <span className={styles.mandatory} aria-hidden>
              *
            </span>
          </label>
          <input
            id="asset-owner-account-holder"
            className={formInputClassName(
              styles.input,
              'account_holder_name',
              directionStyles.inputLtr,
              directionStyles.inputNameField,
            )}
            type="text"
            dir={formInputDir('account_holder_name', language)}
            lang={language === 'he' ? 'he' : undefined}
            autoComplete="name"
            value={payload.account_holder_name == null ? '' : String(payload.account_holder_name)}
            disabled={disabled}
            onChange={(event) => patchField('account_holder_name', event.target.value)}
          />
        </div>

        {isIsrael ? (
          <>
            <div className={`${styles.grid} ${styles.gridTwoCol}`}>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="asset-owner-bank-number">
                  {t('register.assetOwnerPayout.bankNumber')}
                  <span className={styles.mandatory} aria-hidden>
                    *
                  </span>
                </label>
                <input
                  id="asset-owner-bank-number"
                  className={`${styles.input} ${directionStyles.inputLtr}`}
                  type="text"
                  dir="ltr"
                  inputMode="numeric"
                  autoComplete="off"
                  maxLength={2}
                  value={fields.bank_number}
                  disabled={disabled}
                  onChange={(event) => patchField('bank_number', digitsOnly(event.target.value))}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="asset-owner-branch-number">
                  {t('register.assetOwnerPayout.branchNumber')}
                  <span className={styles.mandatory} aria-hidden>
                    *
                  </span>
                </label>
                <input
                  id="asset-owner-branch-number"
                  className={`${styles.input} ${directionStyles.inputLtr}`}
                  type="text"
                  dir="ltr"
                  inputMode="numeric"
                  autoComplete="off"
                  maxLength={3}
                  value={fields.branch_number}
                  disabled={disabled}
                  onChange={(event) => patchField('branch_number', digitsOnly(event.target.value))}
                />
              </div>
            </div>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="asset-owner-account-number-il">
                {t('register.assetOwnerPayout.accountNumber')}
                <span className={styles.mandatory} aria-hidden>
                  *
                </span>
              </label>
              <input
                id="asset-owner-account-number-il"
                className={`${styles.input} ${directionStyles.inputLtr}`}
                type="text"
                dir="ltr"
                inputMode="numeric"
                autoComplete="off"
                value={fields.account_number}
                disabled={disabled || accountNumberLocked}
                readOnly={accountNumberLocked}
                onChange={(event) => patchField('account_number', digitsOnly(event.target.value))}
              />
            </div>
          </>
        ) : (
          <>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="asset-owner-routing-number">
                {t('register.assetOwnerPayout.routingNumber')}
                <span className={styles.mandatory} aria-hidden>
                  *
                </span>
              </label>
              <input
                id="asset-owner-routing-number"
                className={`${styles.input} ${directionStyles.inputLtr}`}
                type="text"
                dir="ltr"
                inputMode="numeric"
                autoComplete="off"
                maxLength={9}
                value={fields.routing_number}
                disabled={disabled}
                onChange={(event) => patchField('routing_number', digitsOnly(event.target.value))}
              />
            </div>
            <div className={`${styles.grid} ${styles.gridTwoCol}`}>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="asset-owner-account-number-us">
                  {t('register.assetOwnerPayout.accountNumber')}
                  <span className={styles.mandatory} aria-hidden>
                    *
                  </span>
                </label>
                <input
                  id="asset-owner-account-number-us"
                  className={`${styles.input} ${directionStyles.inputLtr}`}
                  type="text"
                  dir="ltr"
                  inputMode="numeric"
                  autoComplete="off"
                  value={fields.account_number}
                  disabled={disabled || accountNumberLocked}
                  readOnly={accountNumberLocked}
                  onChange={(event) => patchField('account_number', digitsOnly(event.target.value))}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="asset-owner-account-type">
                  {t('register.assetOwnerPayout.accountType')}
                  <span className={styles.mandatory} aria-hidden>
                    *
                  </span>
                </label>
                <select
                  id="asset-owner-account-type"
                  className={styles.select}
                  value={fields.account_type}
                  disabled={disabled}
                  onChange={(event) =>
                    patchField('account_type', event.target.value as AssetOwnerUsAccountType | '')
                  }
                >
                  <option value="">{t('register.assetOwnerPayout.selectAccountType')}</option>
                  {ASSET_OWNER_US_ACCOUNT_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {t(
                        type === 'checking'
                          ? 'register.assetOwnerPayout.accountTypeChecking'
                          : 'register.assetOwnerPayout.accountTypeSavings',
                      )}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </>
        )}
      </div>

      <div className={styles.field}>
        <label className={styles.label} htmlFor="asset-owner-payout-transfer-day">
          {t('register.assetOwnerPayout.transferDay')}
          <span className={styles.mandatory} aria-hidden>
            *
          </span>
        </label>
        <select
          id="asset-owner-payout-transfer-day"
          className={styles.select}
          value={fields.payout_transfer_day === '' ? '' : String(fields.payout_transfer_day)}
          disabled={disabled}
          onChange={(event) => {
            const raw = event.target.value;
            patchField('payout_transfer_day', raw === '' ? '' : Number(raw));
          }}
        >
          <option value="">{t('register.assetOwnerPayout.selectTransferDay')}</option>
          {ASSET_OWNER_PAYOUT_TRANSFER_DAYS.map((day) => {
            const optionKey = assetOwnerPayoutTransferDayOptionKey(day);
            return (
              <option key={day} value={day}>
                {optionKey ? t(optionKey) : day}
              </option>
            );
          })}
        </select>
      </div>
    </section>
  );
}
